import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getItem, setItem } from 'utils/localStorage';
import axiosInstance from '../axiosConfig';

const useAlertSessionExpiry = () => {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const isMountedRef = useRef(true);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(180);
  const [error, setError] = useState(null);

  const SESSION_TIMEOUT = 3600; // 60 mins
  const WARNING_THRESHOLD = 3420; // 57 mins

  const checkSessionExpiry = useCallback(() => {
    try {
      if (!isMountedRef.current) return;

      const refreshedAt = getItem('refreshed_at');
      const currentTime = Math.floor(Date.now() / 1000);
      const timeElapsed = refreshedAt ? currentTime - parseInt(refreshedAt, 10) : 0;

      if (timeElapsed >= SESSION_TIMEOUT) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        setItem('access_token', null);
        setItem('refreshed_at', null);
        setShowWarning(false);
        navigate('/auth/sign-in', { replace: true });
        return;
      }

      if (timeElapsed >= WARNING_THRESHOLD && !showWarning) {
        setShowWarning(true);
        countdownRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (!isMountedRef.current) {
              clearInterval(countdownRef.current);
              return prev;
            }
            if (prev <= 1) {
              clearInterval(countdownRef.current);
              setItem('access_token', null);
              setItem('refreshed_at', null);
              setShowWarning(false);
              navigate('/auth/sign-in', { replace: true });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (err) {
      console.error('Error in checkSessionExpiry:', err);
      if (isMountedRef.current) {
        setError('An error occurred while checking session. Please try again.');
      }
    }
  }, [navigate, showWarning]);

  const handleLogout = useCallback(() => {
    try {
      if (!isMountedRef.current) return;
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setShowWarning(false);
      setItem('access_token', null);
      setItem('refreshed_at', null);
      navigate('/auth/sign-in', { replace: true });
    } catch (err) {
      console.error('Error in handleLogout:', err);
      if (isMountedRef.current) {
        setError('Failed to log out. Please try again.');
      }
    }
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    try {
      if (!isMountedRef.current) return;
      const response = await axiosInstance.post('/refresh-token');
      const { access_token } = response.data;
      const newRefreshTime = Math.floor(Date.now() / 1000);
      setItem('access_token', access_token);
      setItem('refreshed_at', newRefreshTime);

      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);

      setShowWarning(false);
      setCountdown(180);
      setError(null);

      timerRef.current = setInterval(checkSessionExpiry, 10000);
    } catch (err) {
      console.error('Error in handleRefresh:', err);
      if (isMountedRef.current) {
        setError('Failed to refresh session. Please log out and try again.');
        handleLogout();
      }
    }
  }, [checkSessionExpiry, handleLogout]);

  useEffect(() => {
    isMountedRef.current = true;
    try {
      timerRef.current = setInterval(checkSessionExpiry, 10000);
      checkSessionExpiry();

      return () => {
        isMountedRef.current = false;
        if (timerRef.current) clearInterval(timerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    } catch (err) {
      console.error('Error in useEffect:', err);
      if (isMountedRef.current) {
        setError('An error occurred in session expiry setup.');
      }
    }
  }, [checkSessionExpiry]);

  return (
    <>
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-navy-800 rounded-xl p-6 shadow-lg max-w-sm w-full">
            <p className="text-center text-navy-700 dark:text-white mb-4">
              Your session is about to expire, you will be logged out if you don't refresh!
            </p>
            <p className="text-center text-navy-700 dark:text-white mb-4">
              Time remaining: {countdown} seconds
            </p>
            {error && <p className="text-center text-red-500 mb-4">{error}</p>}
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg text-base font-medium transition duration-200"
              >
                Log Out
              </button>
              <button
                onClick={handleRefresh}
                className="bg-brand-500 hover:bg-brand-600 text-white py-2 px-4 rounded-lg text-base font-medium transition duration-200"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default useAlertSessionExpiry;
